import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ReactComponent as ExpandIcon } from "../../assets/icons/expandIconDVF.svg";
import { resolveImageGeneratorResultSrc } from "../../app/api/imageGenerator";
import {
  EMPTY_SKETCH_ASSETS,
  SilhouetteSketchAsset,
  collectionBuildDividerClassName,
} from "./collectionBuildConstants";

type SketchDeck = {
  assets: SilhouetteSketchAsset[];
  loading?: boolean;
  selectedIds: string[];
  emptyMessage?: string;
  loadingMessage?: string;
};

type Props = {
  open: boolean;
  assets: SilhouetteSketchAsset[];
  loading?: boolean;
  selectedIds: string[];
  /**
   * Collection Build keeps multi-select. Image Generation passes "single"
   * so each deck keeps one selection.
   */
  selectionMode?: "multiple" | "single";
  onClose: () => void;
  onSelect: (ids: string[]) => void;
  /**
   * Optional second deck. Image Generation passes this with `combineDecks`
   * so silhouettes and details stay separated (toggle).
   * Collection Build omits this and keeps multi-select.
   */
  alternateDeck?: SketchDeck;
  primaryEmptyMessage?: string;
  primaryLoadingMessage?: string;
  /** Label for the primary deck when `combineDecks` is on. */
  primaryDeckTitle?: string;
  /** Label for the alternate deck when `combineDecks` is on. */
  alternateDeckTitle?: string;
  /**
   * Image Generation only. Toggle between `assets` and `alternateDeck`.
   * Single-select per deck; only the primary (silhouette) deck is required.
   * Collection Build omits this.
   */
  combineDecks?: boolean;
  onSelectDecks?: (selection: {
    primary: string[];
    alternate: string[];
  }) => void;
};

type ViewMode = "grid" | "one";

const GridViewIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <rect x="1.5" y="1.5" width="7" height="7" rx="1" fill="currentColor" />
    <rect x="11.5" y="1.5" width="7" height="7" rx="1" fill="currentColor" />
    <rect x="1.5" y="11.5" width="7" height="7" rx="1" fill="currentColor" />
    <rect x="11.5" y="11.5" width="7" height="7" rx="1" fill="currentColor" />
  </svg>
);

const OneByOneViewIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <rect
      x="3.5"
      y="2"
      width="13"
      height="16"
      rx="1.5"
      stroke="currentColor"
      strokeWidth="1.75"
    />
  </svg>
);

const NavChevronIcon: React.FC<{ direction: "left" | "right" }> = ({
  direction,
}) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d={direction === "left" ? "M15 5 8 12l7 7" : "m9 5 7 7-7 7"}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Same check glyph as ImageGeneratorDesignSheetDownloadModal. */
const SelectedCheckIcon: React.FC = () => (
  <svg width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden="true">
    <path
      d="M1 4.2 3.6 6.8 9 1.2"
      stroke="#FFFFFF"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const limitDraft = (ids: string[], selectionMode: "multiple" | "single") =>
  selectionMode === "single" ? ids.slice(0, 1) : ids;

const CollectionBuildSketchModal: React.FC<Props> = ({
  open,
  assets = EMPTY_SKETCH_ASSETS,
  loading = false,
  selectedIds,
  selectionMode = "multiple",
  onClose,
  onSelect,
  alternateDeck,
  primaryEmptyMessage,
  primaryLoadingMessage,
  primaryDeckTitle = "Silhouette",
  alternateDeckTitle = "Detailed",
  combineDecks = false,
  onSelectDecks,
}) => {
  const [primaryDraft, setPrimaryDraft] = useState<string[]>(selectedIds);
  const [alternateDraft, setAlternateDraft] = useState<string[]>(
    alternateDeck?.selectedIds ?? []
  );
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [enlarged, setEnlarged] = useState<{
    src: string;
    label: string;
  } | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [oneIndex, setOneIndex] = useState(0);
  const [activeCombineDeck, setActiveCombineDeck] = useState<
    "primary" | "alternate"
  >("primary");
  const wasOpenRef = useRef(false);
  const alternateAssets = alternateDeck?.assets;
  const alternateSelectedIds = alternateDeck?.selectedIds;
  const selectedKey = selectedIds.join("\u0000");
  const alternateSelectedKey = (alternateSelectedIds ?? []).join("\u0000");
  const combineDecksActive = Boolean(combineDecks && alternateDeck);
  const primaryGroupAssets = assets;
  const alternateGroupAssets = alternateAssets || EMPTY_SKETCH_ASSETS;
  const alternateOnlyStems = useMemo(() => {
    if (!combineDecksActive) {
      return null;
    }
    const primaryStems = new Set(primaryGroupAssets.map(asset => asset.stem));
    const stems = new Set<string>();
    for (const asset of alternateGroupAssets) {
      if (!primaryStems.has(asset.stem)) {
        stems.add(asset.stem);
      }
    }
    return stems;
  }, [combineDecksActive, primaryGroupAssets, alternateGroupAssets]);
  const visibleAssets = combineDecksActive
    ? activeCombineDeck === "primary"
      ? primaryGroupAssets
      : alternateGroupAssets
    : assets;
  const visibleLoading = combineDecksActive
    ? Boolean(loading || alternateDeck?.loading)
    : loading;
  const loadingMessage =
    (combineDecksActive
      ? primaryLoadingMessage || alternateDeck?.loadingMessage
      : primaryLoadingMessage) || "Loading silhouettes…";
  const primaryDeckEmptyMessage =
    primaryEmptyMessage ||
    "No silhouettes for this season, market, and category.";
  const alternateDeckEmptyMessage =
    alternateDeck?.emptyMessage || "No detailed sketches for these filters.";
  const emptyMessage = combineDecksActive
    ? activeCombineDeck === "primary"
      ? primaryDeckEmptyMessage
      : alternateDeckEmptyMessage
    : primaryEmptyMessage ||
      "No silhouettes for this season, market, and category.";

  useEffect(() => {
    const idsFromKey = (key: string) =>
      limitDraft(key ? key.split("\u0000") : [], selectionMode);
    if (!open) {
      wasOpenRef.current = false;
      setEnlarged(null);
      setViewMode("grid");
      setOneIndex(0);
      setActiveCombineDeck("primary");
      return;
    }
    const opening = !wasOpenRef.current;
    wasOpenRef.current = true;
    setPrimaryDraft(idsFromKey(selectedKey));
    setAlternateDraft(idsFromKey(alternateSelectedKey));
    if (!opening) {
      return;
    }
    setViewMode("grid");
    setOneIndex(0);
    setActiveCombineDeck("primary");
  }, [open, selectedKey, alternateSelectedKey, selectionMode]);

  useEffect(() => {
    if (visibleAssets.length === 0) {
      setOneIndex(0);
      return;
    }
    setOneIndex(prev => Math.min(prev, visibleAssets.length - 1));
  }, [visibleAssets]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }
      if (enlarged) {
        setEnlarged(null);
        return;
      }
      onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, enlarged]);

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    const created = new Map<string, string>();
    const seen = new Set<string>();
    const queue: SilhouetteSketchAsset[] = [];
    for (const asset of alternateAssets
      ? [...assets, ...alternateAssets]
      : assets) {
      if (seen.has(asset.stem)) {
        continue;
      }
      seen.add(asset.stem);
      queue.push(asset);
    }
    setPreviews({});
    (async () => {
      await Promise.all(
        queue.map(async asset => {
          try {
            const preview = await resolveImageGeneratorResultSrc(asset.url);
            if (cancelled) {
              if (preview.startsWith("blob:")) {
                URL.revokeObjectURL(preview);
              }
              return;
            }
            if (preview.startsWith("blob:")) {
              created.set(asset.stem, preview);
            }
            setPreviews(prev => ({ ...prev, [asset.stem]: preview }));
          } catch {
            // Keep the card as a placeholder if the blob cannot be fetched.
          }
        })
      );
    })();
    return () => {
      cancelled = true;
      created.forEach(url => URL.revokeObjectURL(url));
    };
  }, [open, assets, alternateAssets]);

  if (!open) {
    return null;
  }

  const draftForStem = (id: string): "primary" | "alternate" => {
    if (alternateOnlyStems?.has(id)) {
      return "alternate";
    }
    return "primary";
  };

  const selectionIncludes = (id: string) =>
    (draftForStem(id) === "alternate" ? alternateDraft : primaryDraft).includes(
      id
    );

  const toggleId = (id: string) => {
    const update = (prev: string[]) => {
      if (selectionMode === "single") {
        return prev[0] === id ? [] : [id];
      }
      return prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id];
    };
    if (draftForStem(id) === "alternate") {
      setAlternateDraft(update);
      return;
    }
    setPrimaryDraft(update);
  };

  const selectedCount = combineDecksActive
    ? primaryDraft.length + alternateDraft.length
    : primaryDraft.length;
  /** Image Gen: silhouette (primary) required; detailed (alternate) optional. */
  const primarySelectionReady =
    primaryGroupAssets.length === 0 || primaryDraft.length === 1;
  const canConfirmSelection = combineDecksActive
    ? selectionMode === "single"
      ? primarySelectionReady
      : selectedCount > 0
    : true;
  const currentAsset = visibleAssets[oneIndex];
  const currentLabel = currentAsset
    ? currentAsset.label || currentAsset.stem
    : "";
  const currentSelected = currentAsset
    ? selectionIncludes(currentAsset.stem)
    : false;

  const openEnlarge = (event: React.MouseEvent, src: string, label: string) => {
    event.stopPropagation();
    event.preventDefault();
    setEnlarged({ src, label });
  };

  const renderSketchCard = (
    asset: SilhouetteSketchAsset,
    options: { large?: boolean } = {}
  ) => {
    const selected = selectionIncludes(asset.stem);
    const preview = previews[asset.stem];
    const label = asset.label || asset.stem;
    const large = options.large === true;

    return (
      <div
        key={asset.stem}
        className={`relative rounded-xl bg-white transition-[box-shadow,border-color] ${
          selected
            ? "border-[3px] border-[#C4A090] shadow-[0_0_0_3px_rgba(211,192,182,0.55)]"
            : "border-2 border-[#E2E2E2] hover:border-[#D3C0B6]"
        } ${large ? "w-full overflow-hidden" : "overflow-hidden"}`}
      >
        <button
          type="button"
          aria-pressed={selected}
          aria-label={`Select ${label}`}
          onClick={() => toggleId(asset.stem)}
          className="relative block w-full text-left focus:outline-none"
        >
          {large ? (
            <span className="relative block w-full bg-white px-4 pb-6 pt-4">
              {preview ? (
                <img
                  src={preview}
                  alt=""
                  className="mx-auto block h-auto w-full max-h-[min(70vh,48rem)] max-w-full object-contain"
                />
              ) : (
                <span className="flex min-h-[12rem] w-full items-center justify-center px-3 text-center text-[11px] text-tertiary">
                  FLAT SKETCH
                </span>
              )}
              <span className="pointer-events-none absolute left-3 top-2.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-secondary">
                FLAT SKETCH
              </span>
              {selected ? (
                <span className="pointer-events-none absolute right-3 top-2.5 flex h-7 w-7 items-center justify-center rounded-full bg-[#8B6B5C] shadow-md ring-2 ring-white">
                  <SelectedCheckIcon />
                </span>
              ) : null}
            </span>
          ) : (
            <span className="relative block aspect-[4/3] w-full overflow-hidden bg-white">
              {preview ? (
                <img
                  src={preview}
                  alt=""
                  className="h-full w-full object-contain p-2"
                />
              ) : (
                <span className="flex h-full w-full items-center justify-center px-3 text-center text-[11px] text-tertiary">
                  FLAT SKETCH
                </span>
              )}
              <span className="pointer-events-none absolute left-2.5 top-2.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-secondary">
                FLAT SKETCH
              </span>
              {selected ? (
                <>
                  <span className="pointer-events-none absolute inset-0 bg-[#8B6B5C]/32" />
                  <span className="pointer-events-none absolute right-2.5 top-2.5 flex h-7 w-7 items-center justify-center rounded-full bg-[#8B6B5C] shadow-md ring-2 ring-white">
                    <SelectedCheckIcon />
                  </span>
                  <span className="pointer-events-none absolute bottom-2.5 left-2.5 rounded-full bg-[#8B6B5C] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.04em] text-white shadow-sm">
                    Selected
                  </span>
                </>
              ) : null}
            </span>
          )}
        </button>
        {preview ? (
          <button
            type="button"
            aria-label={`Enlarge ${label}`}
            onClick={event => openEnlarge(event, preview, label)}
            className="absolute bottom-2 right-2 z-[1] flex h-7 w-7 items-center justify-center rounded-full border border-[#E2E2E2] bg-white/95 text-secondary shadow-sm hover:bg-white hover:text-primary focus:outline-none"
          >
            <ExpandIcon className="h-3.5 w-3.5 [&_path]:stroke-current" />
          </button>
        ) : null}
      </div>
    );
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[80] flex items-center justify-center font-avenir"
        role="dialog"
        aria-modal="true"
        aria-labelledby="collection-build-select-files-title"
      >
        <div className="fixed inset-0 bg-[#1A1A1A]/85" onClick={onClose} />
        <div
          className="relative z-10 flex max-h-[calc(100vh-1rem)] w-[min(92vw,72rem)] flex-col overflow-hidden rounded-2xl bg-white px-5 py-5 shadow-[0_16px_48px_rgba(0,0,0,0.28)]"
          onClick={event => event.stopPropagation()}
        >
          <div
            className={`flex items-center justify-between gap-3 border-b ${collectionBuildDividerClassName} pb-4`}
          >
            <h2
              id="collection-build-select-files-title"
              className="text-[16px] font-semibold leading-[22px] text-primary"
            >
              Select File(s)
            </h2>
            <div className="flex min-w-0 flex-wrap items-center justify-end gap-3">
              {combineDecksActive ? (
                <div
                  className="flex shrink-0 rounded-full border border-[#E2E2E2] p-1"
                  role="tablist"
                  aria-label="Sketch deck"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeCombineDeck === "primary"}
                    onClick={() => {
                      setActiveCombineDeck("primary");
                      setOneIndex(0);
                    }}
                    className={`h-8 rounded-full px-3 text-sm font-semibold focus:outline-none ${
                      activeCombineDeck === "primary"
                        ? "bg-black text-white"
                        : "text-secondary hover:text-primary"
                    }`}
                  >
                    {primaryDeckTitle}
                    {selectionMode === "single" ? " *" : ""}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeCombineDeck === "alternate"}
                    onClick={() => {
                      setActiveCombineDeck("alternate");
                      setOneIndex(0);
                    }}
                    className={`h-8 rounded-full px-3 text-sm font-semibold focus:outline-none ${
                      activeCombineDeck === "alternate"
                        ? "bg-black text-white"
                        : "text-secondary hover:text-primary"
                    }`}
                  >
                    {alternateDeckTitle}
                  </button>
                </div>
              ) : null}
              {visibleAssets.length > 0 ? (
                <div
                  className="flex shrink-0 rounded-full border border-[#E2E2E2] p-1"
                  role="group"
                  aria-label="Sketch view mode"
                >
                  <button
                    type="button"
                    title="Grid"
                    aria-label="Grid"
                    aria-pressed={viewMode === "grid"}
                    onClick={() => setViewMode("grid")}
                    className={`flex h-10 w-10 items-center justify-center rounded-full focus:outline-none ${
                      viewMode === "grid"
                        ? "bg-black text-white"
                        : "text-secondary hover:text-primary"
                    }`}
                  >
                    <GridViewIcon />
                  </button>
                  <button
                    type="button"
                    title="One by one"
                    aria-label="One by one"
                    aria-pressed={viewMode === "one"}
                    onClick={() => setViewMode("one")}
                    className={`flex h-10 w-10 items-center justify-center rounded-full focus:outline-none ${
                      viewMode === "one"
                        ? "bg-black text-white"
                        : "text-secondary hover:text-primary"
                    }`}
                  >
                    <OneByOneViewIcon />
                  </button>
                </div>
              ) : null}
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto pt-4 pr-1">
            {visibleLoading && visibleAssets.length === 0 ? (
              <p className="py-16 text-center text-sm text-secondary">
                {loadingMessage}
              </p>
            ) : visibleAssets.length === 0 ? (
              <p className="py-16 text-center text-sm text-secondary">
                {emptyMessage}
              </p>
            ) : viewMode === "grid" ? (
              <div className="grid grid-cols-3 gap-3">
                {visibleAssets.map(asset => renderSketchCard(asset))}
              </div>
            ) : currentAsset ? (
              <div className="mx-auto flex w-full flex-col items-center gap-3">
                <div className="flex w-full items-center gap-2">
                  <button
                    type="button"
                    aria-label="Previous sketch"
                    disabled={oneIndex <= 0}
                    onClick={() => setOneIndex(prev => Math.max(0, prev - 1))}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E2E2E2] bg-white text-secondary shadow-sm hover:bg-lightGray hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none"
                  >
                    <NavChevronIcon direction="left" />
                  </button>
                  <div className="min-w-0 flex-1">
                    {renderSketchCard(currentAsset, { large: true })}
                  </div>
                  <button
                    type="button"
                    aria-label="Next sketch"
                    disabled={oneIndex >= visibleAssets.length - 1}
                    onClick={() =>
                      setOneIndex(prev =>
                        Math.min(visibleAssets.length - 1, prev + 1)
                      )
                    }
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E2E2E2] bg-white text-secondary shadow-sm hover:bg-lightGray hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none"
                  >
                    <NavChevronIcon direction="right" />
                  </button>
                </div>
                <p className="text-sm text-secondary">
                  {oneIndex + 1} / {visibleAssets.length}
                </p>
                <p className="text-center text-sm text-tertiary">
                  {currentLabel}
                  {currentSelected ? " · selected" : ""}
                </p>
              </div>
            ) : null}
          </div>
          <div
            className={`mt-5 flex items-center justify-between gap-3 border-t ${collectionBuildDividerClassName} pt-4`}
          >
            <p className="text-sm text-tertiary">
              {combineDecksActive && selectionMode === "single"
                ? `Selected ${primaryDraft.length}/1 ${primaryDeckTitle.toLowerCase()}, ${alternateDraft.length}/1 ${alternateDeckTitle.toLowerCase()} (optional).`
                : `Selected ${selectedCount} ${
                    selectedCount === 1 ? "file" : "files"
                  }.`}
            </p>
            <button
              type="button"
              disabled={!canConfirmSelection}
              onClick={() => {
                if (!canConfirmSelection) {
                  return;
                }
                if (alternateDeck && onSelectDecks) {
                  onSelectDecks({
                    primary: primaryDraft,
                    alternate: alternateDraft,
                  });
                  return;
                }
                onSelect(primaryDraft);
              }}
              className="h-8 rounded-full bg-black px-5 font-avenir text-sm text-white hover:bg-primary focus:outline-none disabled:cursor-not-allowed disabled:opacity-40"
            >
              Select
            </button>
          </div>
        </div>
      </div>
      {enlarged ? (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center font-avenir"
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged flat sketch"
          onClick={event => {
            event.stopPropagation();
            setEnlarged(null);
          }}
        >
          <div className="fixed inset-0 bg-[#1A1A1A]/90" />
          <button
            type="button"
            onClick={event => {
              event.stopPropagation();
              setEnlarged(null);
            }}
            aria-label="Close enlarged sketch"
            className="absolute right-5 top-5 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white text-secondary shadow hover:bg-lightGray focus:outline-none"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 6l12 12M18 6 6 18"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <img
            src={enlarged.src}
            alt={enlarged.label}
            onClick={event => event.stopPropagation()}
            className="relative z-10 max-h-[88vh] max-w-[92vw] rounded-2xl bg-white object-contain shadow-2xl"
          />
        </div>
      ) : null}
    </>,
    document.body
  );
};

export default CollectionBuildSketchModal;
